// Story #13734 (Exec-mailbox S2) — lets a mailbox owner manage their OWN personal-contact list.
// Mail from a personal contact on an owner-linked mailbox is excluded from ingestion/
// classification/storage entirely (EmailIngestionService's gate). RBAC mirrors the API
// (PersonalContactsController: Owner,Admin) — narrower than Email Triage/Spam Viewer
// (Owner,Operations,Admin), since this is the mailbox owner's own personal address book, not a
// shared triage surface; the guard lives one level up, on MyWorkspace.tsx.
import { useState } from 'react';
import {
  DataListShell, useListQuery, type Column, Button, Modal, Input, useToast,
} from '@xcentral/ui';
import type { PersonalContactDto } from '@xcentral/api-client';
import { useApi, ApiError } from '../../hooks/useApi';

// The fields the GET /api/v1/my-workspace/personal-contacts list endpoint returns
// (PersonalContactDtoPagedResult).
export interface PersonalContactRow {
  id: string;
  email: string;
  displayName: string | null;
  createdAt: string;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU');
}

export default function PersonalContactsPanel() {
  const { get, post, del } = useApi();
  const { toast } = useToast();

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', displayName: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const list = useListQuery<PersonalContactRow>({
    fetcher: get,
    endpoint: '/api/v1/my-workspace/personal-contacts',
  });

  const items = list.data?.items ?? [];

  function openAdd() {
    setForm({ email: '', displayName: '' });
    setFormError(null);
    setAdding(true);
  }

  function closeAdd() {
    if (saving) return;
    setAdding(false);
  }

  async function submitAdd() {
    const email = form.email.trim();
    if (!email || !email.includes('@')) {
      setFormError('A valid email address is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      await post<PersonalContactDto>('/api/v1/my-workspace/personal-contacts', {
        email, displayName: form.displayName.trim() || null,
      });
      toast('success', `${email} added to your personal contacts.`);
      setAdding(false);
      list.refetch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setFormError('This contact is already on your personal contacts list.');
      } else {
        setFormError(err instanceof Error ? err.message : 'Failed to add contact.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    setRemovingId(id);
    try {
      await del(`/api/v1/my-workspace/personal-contacts/${id}`);
      toast('success', 'Removed from your personal contacts.');
      list.refetch();
    } catch (err) {
      const message = err instanceof ApiError
        ? (err.body as { detail?: string })?.detail ?? `Action failed (${err.status})`
        : err instanceof Error ? err.message : 'Action failed';
      toast('error', message);
    } finally {
      setRemovingId(null);
    }
  }

  const columns: Column<PersonalContactRow>[] = [
    {
      key: 'email',
      header: 'Contact',
      render: item => (
        <div>
          <div className="text-gray-900 font-medium">{item.displayName || item.email}</div>
          {item.displayName && <div className="text-xs text-gray-500">{item.email}</div>}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Added',
      className: 'whitespace-nowrap',
      render: item => formatTimestamp(item.createdAt),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: item => (
        <Button
          size="sm"
          variant="ghost"
          disabled={removingId === item.id}
          onClick={() => remove(item.id)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <>
      <DataListShell<PersonalContactRow>
        title="Personal contacts"
        description="Mail from these addresses is excluded from case triage entirely"
        itemLabel="contact"
        headerAction={<Button onClick={openAdd}>Add contact</Button>}
        columns={columns}
        data={items}
        keyField="id"
        status={list.status}
        error={list.error}
        onRetry={list.refetch}
        emptyTitle="No personal contacts yet"
        emptyDescription="Add a personal email address to keep it out of ingestion/classification/storage entirely."
        page={list.page}
        totalCount={list.data?.totalCount ?? 0}
        onPageChange={list.setPage}
      />

      {adding && (
        <Modal
          open
          onClose={closeAdd}
          title="Add a personal contact"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={closeAdd} disabled={saving}>Cancel</Button>
              <Button onClick={submitAdd} disabled={saving}>{saving ? 'Adding…' : 'Add'}</Button>
            </div>
          }
        >
          <div className="space-y-3">
            {formError && <p className="text-sm text-red-600" role="alert">{formError}</p>}
            <Input
              label="Email"
              placeholder="name@example.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
            <Input
              label="Name (optional)"
              placeholder="Jane Doe"
              value={form.displayName}
              onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
            />
          </div>
        </Modal>
      )}
    </>
  );
}
